import { useEffect } from 'react';
import { useDrop } from 'react-dnd';
import type { DragTypes } from './constants';
import { MoveStates } from './constants';
import isTopicPostPinned from './utils/isTopicPostPinned';
import type {
  ContentMapDropParams,
  ContentMapGetDropParams,
} from './utils/types';
import type { TopicReorganizationTree } from './useTopicReorganization';

const getTopicDropParams = (
  tree: TopicReorganizationTree,
  accept: DragTypes[],
  getDropParams: ContentMapGetDropParams,
): ContentMapDropParams => {
  const pinnedPosts = tree.topicPosts.filter(isTopicPostPinned);

  return getDropParams(accept, {
    destinationTree: tree,
    indexWithPlaceholder: tree.children.length,
    pinnedPostIndexWithPlaceholder: pinnedPosts.length,
    canDrop: (item) =>
      // pinned post reordering should be handled by TopicTreeSeparator
      [...item.toMove.pinnedPostKeys, ...item.toMove.postKeys].every((key) => {
        const topicPost = tree.topicPosts.find((p) => p.postKey === key);

        return (
          !topicPost ||
          topicPost.reorganizationMetadata.move.state === MoveStates.movedOut
        );
      }),
  });
};

interface UseTopicDropArgs {
  tree: TopicReorganizationTree;
  topicId: string;
  getDropParams: ContentMapGetDropParams;
  onDragOverChange: (
    isDragOver: boolean,
    ops: { key: string; topicId: string },
  ) => void;
}

const useTopicDrop = (
  accept: DragTypes[],
  { tree, topicId, getDropParams, onDragOverChange }: UseTopicDropArgs,
) => {
  const [{ isOver: isDragOver }, dropRef] = useDrop(
    getTopicDropParams(tree, accept, getDropParams),
  );

  useEffect(() => {
    onDragOverChange(isDragOver, {
      key: topicId,
      topicId: tree.topic.id,
    });
  }, [onDragOverChange, isDragOver, topicId, tree]);

  return dropRef;
};

export default useTopicDrop;
